import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { getScan, importScanData, parseScanRecord, updateScanStatus } from './-service'
import { useEffect, useState } from 'react'
import { capitalize, noop } from 'lodash'
import { TableNames } from '@willie/supabase'
import {
  Button,
  Card,
  Flex,
  Heading,
  Text,
  Badge,
  Table,
  Separator,
  DataList,
  HoverCard,
  IconButton,
  Select
} from '@radix-ui/themes'
import * as Collapsible from '@radix-ui/react-collapsible'
import {
  LuBadgeInfo,
  LuChevronRight,
  LuCircle,
  LuCircleCheckBig,
  LuFilePlus2,
  LuPencil,
  LuTriangleAlert
} from 'react-icons/lu'
import cn from 'classnames'
import { listSites, SiteSummary } from '~/features/sites/service'
import { EiffageScan, ScanDataPoint, ScanExtraction } from '@willie/schemas'
import { DateTime } from 'luxon'
import { ScanSummary } from './-components/ScanListItem'
import _ from 'lodash'
import { WaterStation } from '~/features/stations/schemas'
import EditDatapointDialog from './-components/EditDatapointDialog'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import LinkButton from '~/components/LinkButton'

interface DataPointsTableProps {
  points: ScanDataPoint[]
  pumpStations: { id: string; name: string }[]
  siteStations: WaterStation[]
  onPointsChange?: (points: ScanDataPoint[]) => void
}

type ReasoningHoverCardProps = { reasoning: string; hasIncoherence: boolean; className?: string }
const ReasoningHoverCard = ({ reasoning, hasIncoherence, className }: ReasoningHoverCardProps) => {
  if (!reasoning || reasoning.trim() === '') {
    return null
  }

  return (
    <HoverCard.Root>
      <HoverCard.Trigger className={cn('hidden sm:block', className)}>
        {hasIncoherence ? (
          <LuTriangleAlert size={20} className={'text-orange-500'} />
        ) : (
          <LuBadgeInfo size={20} className={'text-blue-500'} />
        )}
      </HoverCard.Trigger>{' '}
      <HoverCard.Content>
        <Text size="2" as="p" weight="bold" mb={'1'}>
          Analyse de la lecture
        </Text>
        <Text size="2">{reasoning}</Text>
      </HoverCard.Content>
    </HoverCard.Root>
  )
}

const DataPointsTable = ({ points, pumpStations, onPointsChange, siteStations }: DataPointsTableProps) => {
  return (
    <Flex direction="column" gap="2">
      <Text size="3" weight="bold">
        Lectures de volume
      </Text>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.RowHeaderCell className="font-medium">Date</Table.RowHeaderCell>
            <Table.RowHeaderCell className="font-medium text-center">Station de Pompage</Table.RowHeaderCell>
            <Table.RowHeaderCell className="font-medium text-center">
              Volume Total{' '}
              <span className="font-normal">
                (m<sup>3</sup>)
              </span>
            </Table.RowHeaderCell>
            <Table.RowHeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {points.map((reading, idx) => (
            <Table.Row
              key={`${reading.date}${reading.pumpStationID}${reading.volume}${reading.reasoning}`}
              className="group align-middle h-14"
            >
              <Table.Cell>
                <Text size="2" className="sm:hidden">
                  {capitalize(
                    DateTime.fromISO(reading.date)
                      .setZone('Europe/Paris')
                      .toLocaleString(DateTime.DATE_SHORT, { locale: 'fr' })
                  )}
                </Text>
                <Text className="hidden sm:block" size="2">
                  {capitalize(
                    DateTime.fromISO(reading.date).setZone('Europe/Paris').toLocaleString(
                      {
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                        year: 'numeric'
                      },
                      { locale: 'fr' }
                    )
                  )}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text className="line-clamp-2 text-center" size="2">
                  {pumpStations.find((station) => station.id === reading.pumpStationID)?.name ||
                    siteStations.find((station) => station.id === reading.pumpStationID)?.name}
                </Text>
              </Table.Cell>
              <Table.Cell className="text-center">{reading.volume}</Table.Cell>
              <Table.Cell>
                <Flex align={'center'} gap={'3'} justify="end">
                  <ReasoningHoverCard
                    reasoning={reading.reasoning}
                    hasIncoherence={reading.hasIncoherence}
                    className={!reading.hasIncoherence ? 'sm:opacity-0 sm:group-hover:opacity-100' : ''}
                  />
                  {!!onPointsChange && (
                    <EditDatapointDialog
                      datapoint={reading}
                      onSave={(updatedReading) => {
                        const newPoints = points.map((point, index) => (index === idx ? updatedReading : point))
                        onPointsChange(newPoints)
                      }}
                      stations={siteStations}
                    >
                      <IconButton variant="ghost" color="green">
                        <LuPencil size={20} />
                      </IconButton>
                    </EditDatapointDialog>
                  )}
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  )
}

const AnimatedSuccessMessage = () => {
  const [revealed, setRevealed] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    setTimeout(() => setInitialized(true), 100) // delay to allow initial render
    setTimeout(() => setRevealed(true), 350) // slight delay for effect
  }, [])

  return (
    <Flex align={'center'} gap="4" className="mx-auto">
      <div className="relative w-10 h-10">
        <LuCircle
          size={40}
          className={cn('absolute text-green-600 mx-auto transition-[opacity] duration-100', {
            'duration-400 delay-100': initialized,
            'opacity-0': !initialized || revealed,
            'opacity-100': initialized && !revealed
          })}
        />
        <LuCircleCheckBig
          size={40}
          className={cn('absolute text-green-600 mx-auto transition-[clip-path] duration-400 ease-in')}
          style={{
            clipPath: revealed ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)'
          }}
        />
      </div>
      <Heading size="6" className={cn('transition-all delay-300', { 'opacity-0': !revealed })}>
        Carnet importé avec succès
      </Heading>
    </Flex>
  )
}

const ScanProcessing = ({ status }: { status: EiffageScan['status'] }) => {
  const messages = [
    'Extraction des données du site et des stations de pompage',
    'Extraction des lectures de volume',
    'Analyse et validation des lectures de volume',
    'Vérification des incohérences',
    'Ajustement des données',
    'Préparation des résultats',
    "Finalisation de l'analyse"
  ]
  const [message, setMessage] = useState(
    status === 'pending' ? 'Traittement en attente' : "Début de l'analyse du carnet de suivi"
  )

  useEffect(() => {
    if (status !== 'processing') return

    let msgIdx = 0
    const interval = setInterval(() => {
      if (msgIdx < messages.length) {
        setMessage(messages[msgIdx++])
      }
    }, 3500) // Simulate processing

    return () => clearInterval(interval)
  }, [status])

  return (
    <Card>
      <Flex justify="center" py="4" align={'center'}>
        <Flex gap="3" align="center">
          <div className="w-[20px] h-[20px] rounded-full bg-pink-400 animate-pulse" />
          <Text size="3" color="gray">
            {message}
          </Text>
        </Flex>
      </Flex>
    </Card>
  )
}

const RouteComponent = () => {
  const { supabaseClient, org } = Route.useRouteContext()
  const [scan, setScan] = useState<EiffageScan | null>(null)
  const [collapsibleOpen, setCollapsibleOpen] = useState(false)

  const { scanID } = Route.useParams()

  const handleReceiveScan = (scan: EiffageScan) => {
    setCollapsibleOpen(scan.status === 'validation')
    if (scan.extractedData && !scan.verifiedData) {
      scan.verifiedData = { ...scan.extractedData }
    }
    setScan(scan)
  }

  const { isLoading, error } = useQuery({
    queryKey: ['plugins', 'scans', scanID],
    queryFn: async () => {
      const scan = await getScan(scanID)
      handleReceiveScan(scan)
      return 0
    }
  })

  useEffect(() => {
    if (scan?.status !== 'processing' || !scan.processedAt) {
      return
    }

    const startedAt = DateTime.fromISO(scan.processedAt)
    const timer = setInterval(async () => {
      if (startedAt.plus({ minutes: 1 }) < DateTime.now()) {
        void updateScanStatus(scan.ID, 'failed')
        clearInterval(timer)
      }
    }, 5000)

    return () => clearInterval(timer)
  }, [scan])

  useEffect(() => {
    const channel = supabaseClient
      .channel(`public:scans:${scanID}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'plugin_eiffage_scans' satisfies TableNames,
          filter: `id=eq.${scanID}`
        },
        (payload: any) => {
          try {
            const parsed = parseScanRecord(payload.new, { partial: true })
            handleReceiveScan({ ...scan, ...parsed } as EiffageScan)
          } catch (error) {
            console.error('Error parsing scan record update:', error)
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`Subscribed to scan updates for ${scanID}`)
        } else {
          console.warn(`Failed to subscribe to scan updates for ${scanID}:`, status)
        }
      })

    return () => {
      supabaseClient.removeChannel(channel).finally(noop)
    }
  }, [scan])

  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ orgId: org.id })
  })

  const { mutate: handleImport, isPending } = useMutation({
    mutationFn: async () => {
      if (!scan || !scan.verifiedData) {
        return
      }
      setCollapsibleOpen(false)
      await importScanData(scan)
      await new Promise((resolve) => setTimeout(resolve, 500)) // delay for effect
    },
    onSuccess: () => {},
    onError: () => {
      toast.error("Erreur lors de l'importation des données du scan", {
        position: 'bottom-center'
      })
    }
  })

  const handleDataPointsChange = (readings: ScanDataPoint[]) => {
    if (!scan?.extractedData || !sites) return
    const newPumpStations = _(readings)
      .map((reading) => {
        const station = sites
          ?.find((site) => site.id === scan.verifiedData!.site.ID)
          ?.stations.find((station) => station.id === reading.pumpStationID)
        if (!station) {
          return null
        }

        return {
          id: station.id,
          name: station.name
        } satisfies ScanExtraction['metadata']['pumpStations'][number]
      })
      .filter((station) => station !== null)
      .uniqBy('id')
      .value()

    const baseData = scan.verifiedData || scan.extractedData
    const updatedScan = {
      ...scan,
      verifiedData: {
        ...baseData,
        metadata: {
          ...baseData.metadata,
          pumpStations: newPumpStations
        },
        readings
      }
    }

    setScan(updatedScan as EiffageScan)
  }

  const handleSiteChange = (site: SiteSummary) => {
    if (!scan?.extractedData || !site) return
    const baseData = scan.verifiedData || scan.extractedData

    const updatedScan = {
      ...scan,
      verifiedData: {
        ...baseData,
        site: {
          ID: site.id,
          name: site.name,
          internalID: site.internal_id ?? '',
          hasIncoherence: false,
          reasoning: `Site chosi manuellement par l'utilisateur.`
        },
        readings: (scan.verifiedData?.readings || scan.extractedData?.readings || []).map((reading) => ({
          ...reading,
          pumpStationID: site.stations[0]?.id
        }))
      }
    } satisfies EiffageScan

    setScan(updatedScan)
  }

  return (
    <Flex direction="column" gap="4" py="4" className="w-full max-w-4xl mx-auto sm:px-6 lg:px-8">
      {scan?.status === 'completed' ? (
        <AnimatedSuccessMessage />
      ) : (
        <Heading size="6" align="center">
          Analyse du carnet de suivi
        </Heading>
      )}

      {isLoading && (
        <Card>
          <Flex justify="center" py="6">
            <Text size="3">Chargement du carnet</Text>
          </Flex>
        </Card>
      )}

      {error && (
        <Card>
          <Flex justify="center" py="4" direction="column" align="center" gap="2">
            <Badge color="red" size="2">
              Erreur
            </Badge>
            <Text color="red">{error.message}</Text>
          </Flex>
        </Card>
      )}

      {scan && (
        <Flex direction="column" gap="4">
          <ScanSummary scan={scan} />

          {['completed', 'validation'].includes(scan.status) && scan.verifiedData ? (
            <>
              <Card>
                <Collapsible.Root open={collapsibleOpen} onOpenChange={setCollapsibleOpen}>
                  <Flex direction="column" gap="4" p={'2'}>
                    <Collapsible.Trigger asChild>
                      <Flex justify="between" align="center">
                        <Heading size="3">Données extraites</Heading>

                        <LuChevronRight
                          size={25}
                          className={cn('transition-all', { ['rotate-90']: collapsibleOpen })}
                        />
                      </Flex>
                    </Collapsible.Trigger>

                    <Collapsible.Content>
                      <Flex direction={'column'} gap={'4'}>
                        <Separator size="4" my={'3'} />
                        <Flex justify={'between'} gap="4" className="flex-wrap">
                          <Flex direction={'column'} gap={'3'} className="w-full sm:w-1/2">
                            <Flex align={'center'} gap="2">
                              <Text size={'3'} weight={'bold'}>
                                Chantier Identifié
                              </Text>
                              <ReasoningHoverCard
                                reasoning={scan.verifiedData.site.reasoning}
                                hasIncoherence={scan.verifiedData.site.hasIncoherence}
                                className="block"
                              />
                            </Flex>
                            {scan.status === 'validation' ? (
                              <Select.Root
                                defaultValue={scan.verifiedData.site.ID}
                                onValueChange={(value) => {
                                  const site = sites?.find((site) => site.id === value)
                                  if (site) {
                                    handleSiteChange(site)
                                  }
                                }}
                              >
                                <Select.Trigger className="w-full" placeholder="Sélectionner un chantier">
                                  {scan.verifiedData.site.name}
                                </Select.Trigger>
                                <Select.Content position="item-aligned" variant="soft" color="gray">
                                  {sites?.map((site) => (
                                    <Select.Item key={site.id} value={site.id}>
                                      <Flex gap="2" align={'center'}>
                                        {site.name}
                                      </Flex>
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            ) : (
                              <Badge size="2" variant="soft" color="gray">
                                {scan.verifiedData.site.name}
                              </Badge>
                            )}
                            <HoverCard.Root>
                              <HoverCard.Trigger className="hidden sm:block">
                                <Text size={'2'} weight={'medium'} className="cursor-pointer" color="blue">
                                  Information supplémentaires
                                </Text>
                              </HoverCard.Trigger>
                              <HoverCard.Content sideOffset={5}>
                                <DataList.Root>
                                  <DataList.Item>
                                    <DataList.Label>Numéro de Semaine</DataList.Label>
                                    <DataList.Value>{scan.verifiedData.metadata.weekNumber}</DataList.Value>
                                  </DataList.Item>
                                  <DataList.Item>
                                    <DataList.Label>Nom du Conducteur</DataList.Label>
                                    <DataList.Value>{scan.verifiedData.metadata.driverName}</DataList.Value>
                                  </DataList.Item>
                                  <DataList.Item>
                                    <DataList.Label>Volume du Réservoir</DataList.Label>
                                    <DataList.Value>{scan.verifiedData.metadata.tankVolume}</DataList.Value>
                                  </DataList.Item>
                                  <DataList.Item>
                                    <DataList.Label>Nombre de Lectures</DataList.Label>
                                    <DataList.Value>{scan.verifiedData.readings.length}</DataList.Value>
                                  </DataList.Item>
                                </DataList.Root>
                              </HoverCard.Content>
                            </HoverCard.Root>
                          </Flex>

                          <Flex direction={'column'} gap={'2'}>
                            <Text size={'3'} weight={'bold'}>
                              Stations de Pompage identifiées
                            </Text>
                            {scan.verifiedData.metadata.pumpStations.map((station) => (
                              <Badge size="2" variant="soft" color="gray" key={station.id}>
                                {station.name}
                              </Badge>
                            ))}
                          </Flex>
                        </Flex>
                        <Separator size="4" my="3" />
                        <DataPointsTable
                          // @ts-ignore
                          siteStations={
                            sites?.find((site) => site.id === scan.verifiedData?.site.ID)?.stations ??
                            ([] as WaterStation[])
                          }
                          points={scan.verifiedData!.readings}
                          pumpStations={scan.verifiedData.metadata.pumpStations}
                          onPointsChange={scan.status === 'validation' ? handleDataPointsChange : undefined}
                        />
                      </Flex>
                    </Collapsible.Content>
                  </Flex>
                </Collapsible.Root>
              </Card>
              {scan.status === 'validation' ? (
                <Button
                  size="3"
                  onClick={() => handleImport()}
                  disabled={!scan.verifiedData}
                  loading={isPending}
                  className="sm:ml-auto"
                >
                  Importer les Données
                </Button>
              ) : (
                <LinkButton
                  leadingIcon={<LuFilePlus2 size={24} />}
                  mt={'4'}
                  size="4"
                  to="/plugins/eiffage-scans/new"
                  text="Importer un nouveau Carnet"
                  variant="soft"
                  className="w-full *:w-full *:p-7"
                />
              )}
            </>
          ) : ['pending', 'processing'].includes(scan.status) ? (
            <ScanProcessing status={scan.status} />
          ) : null}
        </Flex>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/plugins/eiffage-scans/$scanID')({
  component: RouteComponent
})
